import { chakra } from '@chakra-ui/react';

/**
 * A heading that is actually a heading.
 *
 * Levels are named for the role they play, never for a size, so they cannot be confused with the
 * font scale the way the `heading.*` text styles this replaces could: those sat one step off the
 * scale word they shared, so `heading.md` was 20px while `fontSize="md"` was 16.
 *
 * Each level carries the element its role implies, so a call site states one thing and gets both the
 * type and the outline entry. `as` is only written where a site deviates — a brand wordmark or an
 * overlay caption that should stay out of the outline takes `as="p"`.
 *
 * Text that merely looks like a heading is not one: a value beside its label, or a bolded lead-in
 * in a list row, takes `textStyle="body.strong"` instead. Same pixels, but it stays a `p`.
 */
const LEVEL_ELEMENT = {
	celebration: 'h1',
	page: 'h1',
	dialog: 'h2',
	subsection: 'h3',
	section: 'h3',
} as const;

type Level = keyof typeof LEVEL_ELEMENT;

const StyledTitle = chakra('h3', {
	base: {
		fontWeight: 'semibold',
		lineHeight: 'moderate',
	},
	variants: {
		level: {
			celebration: { fontSize: '3xl' },
			page: { fontSize: '2xl' },
			dialog: { fontSize: 'xl' },
			subsection: { fontSize: 'lg' },
			section: { fontSize: 'md' },
		},
	},
	defaultVariants: {
		level: 'section',
	},
});

type TitleProps = Omit<React.ComponentProps<typeof StyledTitle>, 'level'> & { level?: Level };

export const Title = ({ level = 'section', as, ...props }: TitleProps) => (
	<StyledTitle as={as ?? LEVEL_ELEMENT[level]} level={level} {...props} />
);
